/**
 * QGIS-style Raster Information panel — acquisition, resolution, CRS, bands,
 * pixel type, data source, extent. Reads live TiTiler `/cog/info` (cached)
 * and merges layer / converter fallbacks via {@link buildRasterMetadataView}.
 */

import { useEffect, useMemo, useState } from 'react';
import type { Layer } from '@geosoft/viewer-sdk';
import { Callout, Chip, tokens } from '@/design';
import {
  buildRasterMetadataView,
  isRasterMetadataLayer,
  type RasterMetadataRow,
} from '@/components/raster/rasterMetadata';
import {
  fetchTitilerRasterProfile,
  type TitilerRasterProfile,
} from '@/utils/titilerRasterProfile';

export type RasterMetadataPanelProps = {
  readonly layer?: Layer | null;
  /** When true, skip the "not a raster" empty state (ToolCard already gated). */
  readonly embedded?: boolean;
  /**
   * When false, skip the TiTiler `/cog/info` round-trip so selecting a layer
   * does not pay for metadata until the Information section is opened.
   */
  readonly enabled?: boolean;
  readonly 'data-testid'?: string;
};

type LoadState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; profile: TitilerRasterProfile | null }
  | { status: 'error'; message: string };

function MetadataRows({ rows }: { readonly rows: readonly RasterMetadataRow[] }) {
  return (
    <dl
      style={{
        display: 'grid',
        gridTemplateColumns: 'auto 1fr',
        rowGap: tokens.space[1],
        columnGap: tokens.space[2],
        margin: 0,
      }}
    >
      {rows.map((row) => (
        <div key={row.id} style={{ display: 'contents' }}>
          <dt
            style={{
              fontFamily: tokens.font.family.ui,
              fontSize: tokens.font.role.label.size,
              color: tokens.color.ink.secondary,
              margin: 0,
            }}
          >
            {row.label}
          </dt>
          <dd
            title={row.value}
            style={{
              margin: 0,
              fontFamily: row.mono ? tokens.font.family.mono : tokens.font.family.ui,
              fontSize: row.mono
                ? tokens.font.role.engMono.size
                : tokens.font.role.body.size,
              color: tokens.color.ink.primary,
              overflowWrap: 'anywhere',
            }}
          >
            {row.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export function RasterMetadataPanel({
  layer,
  embedded = false,
  enabled = true,
  'data-testid': testId,
}: RasterMetadataPanelProps) {
  const [loadState, setLoadState] = useState<LoadState>({ status: 'idle' });

  const tilesUrl = useMemo(() => {
    const tiles = layer?.endpoints?.tiles ?? layer?.endpoints?.terrain;
    return typeof tiles === 'string' && tiles.includes('/cog/') ? tiles : null;
  }, [layer]);

  useEffect(() => {
    if (!enabled) {
      setLoadState({ status: 'idle' });
      return;
    }
    if (!tilesUrl) {
      setLoadState({ status: 'ready', profile: null });
      return;
    }
    let cancelled = false;
    setLoadState({ status: 'loading' });
    void fetchTitilerRasterProfile(tilesUrl).then((profile) => {
      if (cancelled) return;
      setLoadState({ status: 'ready', profile });
    }).catch((err) => {
      if (cancelled) return;
      setLoadState({
        status: 'error',
        message: err instanceof Error ? err.message : 'request failed',
      });
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, tilesUrl]);

  if (!isRasterMetadataLayer(layer)) {
    if (embedded) return null;
    return (
      <Callout severity="info" icon="info" title="Raster information">
        Select a raster or terrain COG to inspect its properties.
      </Callout>
    );
  }

  if (!enabled) {
    return (
      <Callout severity="info" icon="info" title="Raster information">
        Expand this section to read the COG header.
      </Callout>
    );
  }

  const profile = loadState.status === 'ready' ? loadState.profile : null;
  const view = buildRasterMetadataView(layer, profile);
  // While loading, still show layer-side fallbacks so the panel is never blank.
  const fallbackView = buildRasterMetadataView(layer, null);
  const rows = view.rows.length > 0 ? view.rows : fallbackView.rows;

  return (
    <div
      data-testid={testId ?? 'raster-metadata-panel'}
      aria-label="Raster information"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.space[2],
        padding: tokens.space[2],
        border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
        borderRadius: tokens.radius.md,
        background: tokens.color.surface[1],
      }}
    >
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: tokens.space[1],
        }}
      >
        {loadState.status === 'loading' ? (
          <Chip icon="pending" variant="info" label="Reading COG header…" />
        ) : null}
        {loadState.status === 'ready' && view.fromTitiler ? (
          <Chip icon="check_circle" variant="success" label="From COG header" />
        ) : null}
        {loadState.status === 'ready' && !view.fromTitiler && tilesUrl ? (
          <Chip icon="info" variant="tertiary" label="Layer metadata only" />
        ) : null}
        {loadState.status === 'error' ? (
          <Chip icon="warning" variant="warning" label="Header unavailable" />
        ) : null}
      </div>

      {rows.length === 0 ? (
        <Callout severity="info" icon="info">
          No raster properties available for this layer yet.
        </Callout>
      ) : (
        <MetadataRows rows={rows} />
      )}

      {loadState.status === 'error' ? (
        <Callout severity="warning" icon="warning" title="COG header unread">
          {loadState.message}. Showing layer / converter fields only.
        </Callout>
      ) : null}
    </div>
  );
}
